function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function applyInlineFormatting(value: string): string {
  return escapeHtml(value)
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/(\*\*\*|___)(.+?)\1/g, '<strong>$2</strong>')
    .replace(/(\*\*|__)(.+?)\1/g, '<strong>$2</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
}

function normalizeContent(raw: string): string {
  return raw
    .replace(/\r\n?/g, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .split('\n')
    .filter((line) => !line.trim().startsWith('```'))
    .map((line) => line.replace(/^\s*>\s?/, ''))
    .filter((line) => !/^\s{0,3}(?:[-*_]\s*){3,}\s*$/.test(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function stripWorkflowMarkdown(raw?: string | null): string {
  if (!raw || !raw.trim()) {
    return ''
  }

  const normalized = normalizeContent(raw)
  if (!normalized) {
    return ''
  }

  return normalized
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*([-*+]|\d+\.)\s+/gm, '')
    .replace(/^\s*\|?\s*:?-{3,}:?(?:\s*\|\s*:?-{3,}:?)+\s*\|?\s*$/gm, '')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
    .replace(/___(.+?)___/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^\s*\|/gm, '')
    .replace(/\|\s*$/gm, '')
    .replace(/\s*\|\s*/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .replace(/\s+/g, ' ')
    .trim()
}

function splitBlocks(value: string): string[][] {
  const blocks: string[][] = []
  let current: string[] = []

  for (const line of value.split('\n')) {
    if (!line.trim()) {
      if (current.length) {
        blocks.push(current)
        current = []
      }
      continue
    }
    current.push(line)
  }

  if (current.length) {
    blocks.push(current)
  }

  return blocks
}

function isTableBlock(lines: string[]): boolean {
  if (lines.length < 2) {
    return false
  }

  const hasPipeRows = lines.every((line) => line.trim().startsWith('|') && line.trim().endsWith('|'))
  const separator = lines[1]?.replace(/[|\s:-]/g, '') === ''
  return hasPipeRows && separator
}

function isListBlock(lines: string[]): boolean {
  return lines.every((line) => /^\s*([*-]|\d+\.)\s+/.test(line))
}

function renderTable(lines: string[]): string {
  const rows = lines.map((line) => line.trim().slice(1, -1).split('|').map((cell) => cell.trim()))
  const headers = rows[0] || []
  const bodyRows = rows.slice(2)

  return [
    '<div class="workflow-markdown-table">',
    '<table>',
    '<thead><tr>',
    headers.map((cell) => `<th>${applyInlineFormatting(cell)}</th>`).join(''),
    '</tr></thead>',
    '<tbody>',
    bodyRows
      .map((row) => `<tr>${row.map((cell) => `<td>${applyInlineFormatting(cell)}</td>`).join('')}</tr>`)
      .join(''),
    '</tbody>',
    '</table>',
    '</div>',
  ].join('')
}

function renderList(lines: string[]): string {
  const ordered = lines.every((line) => /^\s*\d+\.\s+/.test(line))
  const tag = ordered ? 'ol' : 'ul'
  const items = lines
    .map((line) => line.replace(/^\s*([*-]|\d+\.)\s+/, '').trim())
    .map((item) => `<li>${applyInlineFormatting(item)}</li>`)
    .join('')

  return `<${tag}>${items}</${tag}>`
}

function renderParagraph(lines: string[]): string {
  return lines
    .map((line) => {
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
      if (headingMatch) {
        const level = Math.min(headingMatch[1].length + 1, 6)
        return `<h${level}>${applyInlineFormatting(headingMatch[2].trim())}</h${level}>`
      }
      return `<p>${applyInlineFormatting(line)}</p>`
    })
    .join('')
}

export function formatWorkflowRichText(raw?: string | null): string {
  if (!raw || !raw.trim()) {
    return ''
  }

  const normalized = normalizeContent(raw)
  if (!normalized) {
    return ''
  }

  return splitBlocks(normalized)
    .map((block) => {
      if (isTableBlock(block)) {
        return renderTable(block)
      }
      if (isListBlock(block)) {
        return renderList(block)
      }
      return renderParagraph(block)
    })
    .join('')
}